/**
 * AccountSettings - 账号设置 Tab
 */
import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { User, Lock, Mail, Smartphone, Camera } from 'lucide-react';
import Button from '../../../components/ui/Button';

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

export default function AccountSettings() {
  const { user, updateUser } = useAuth();
  const [activeSection, setActiveSection] = useState<'profile' | 'password' | 'bind'>('profile');

  const [profileForm, setProfileForm] = useState({
    nickname: user?.nickname || '',
    avatar: user?.avatar || '',
  });

  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const updateProfile = async () => {
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(profileForm),
      });
      const data = await response.json();
      if (data.success) {
        updateUser(data.data);
        setMessage('保存成功');
      } else {
        setMessage(data.error || '保存失败');
      }
    } catch (error) {
      setMessage('网络错误');
    } finally {
      setLoading(false);
    }
  };

  const updatePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setMessage('两次输入的密码不一致');
      return;
    }
    setLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/user/password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          oldPassword: passwordForm.oldPassword,
          newPassword: passwordForm.newPassword,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setMessage('密码修改成功');
        setPasswordForm({ oldPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setMessage(data.error || '修改失败');
      }
    } catch (error) {
      setMessage('网络错误');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {/* 标签切换 */}
      <div className="p-1 bg-slate-100 rounded-xl flex gap-2">
        {[
          { id: 'profile', label: '基本信息', icon: User },
          { id: 'password', label: '修改密码', icon: Lock },
          { id: 'bind', label: '账号绑定', icon: Mail },
        ].map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveSection(item.id as typeof activeSection)}
            className={`flex-1 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
              activeSection === item.id
                ? 'bg-white text-indigo-600 shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </button>
        ))}
      </div>

      {message && (
        <div className={'rounded-xl text-sm p-4 ' + (message.includes('成功') ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600')}>
          {message}
        </div>
      )}

      {/* 基本信息 */}
      {activeSection === 'profile' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-20 h-20 rounded-full bg-indigo-600 flex items-center justify-center text-white text-2xl font-bold overflow-hidden">
                {profileForm.avatar ? (
                  <img src={profileForm.avatar} alt="avatar" className="w-full h-full object-cover" />
                ) : (
                  user?.nickname?.[0]?.toUpperCase() || 'U'
                )}
              </div>
              <button className="absolute -bottom-1 -right-1 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-slate-600 hover:text-indigo-600">
                <Camera className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <h4 className="font-medium text-slate-800">头像</h4>
              <p className="text-sm text-slate-500">支持 JPG、PNG 格式，最大 2MB</p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-slate-700">昵称</label>
            <input
              type="text"
              value={profileForm.nickname}
              onChange={(e) => setProfileForm(prev => ({ ...prev, nickname: e.target.value }))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          <Button onClick={updateProfile} isLoading={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
            保存修改
          </Button>
        </div>
      )}

      {/* 修改密码 */}
      {activeSection === 'password' && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-slate-700">原密码</label>
            <input
              type="password"
              value={passwordForm.oldPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, oldPassword: e.target.value }))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-slate-700">新密码</label>
            <input
              type="password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label className="block text-sm font-medium text-slate-700">确认新密码</label>
            <input
              type="password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
            />
          </div>

          <Button onClick={updatePassword} isLoading={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
            修改密码
          </Button>
        </div>
      )}

      {/* 账号绑定 */}
      {activeSection === 'bind' && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Mail className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-800">邮箱</h4>
                <p className="text-sm text-slate-500">{user?.email || '未绑定'}</p>
              </div>
            </div>
            <button
              onClick={() => alert('邮箱绑定功能开发中，敬请期待')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-all px-4 py-2"
            >
              {user?.email ? '更换' : '绑定'}
            </button>
          </div>

          <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4 gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-slate-800">手机号</h4>
                <p className="text-sm text-slate-500">{user?.phone || '未绑定'}</p>
              </div>
            </div>
            <button
              onClick={() => alert('手机号绑定功能开发中，敬请期待')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-xl transition-all px-4 py-2"
            >
              {user?.phone ? '更换' : '绑定'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
